import React, { useState, useEffect } from 'react';
import { ErrorBanner } from '../components/UIStates';

const WORKER = process.env.REACT_APP_WORKER_URL || 'https://contenuia-proxy.soihibaliousseni.workers.dev';
const YOUTUBE_CLIENT_ID = process.env.REACT_APP_YOUTUBE_CLIENT_ID || '1013325912163-nkhsn6257f5chl04nrg3ijcbrb28okb0.apps.googleusercontent.com';

const YouTubePage: React.FC = () => {
  const [connected, setConnected] = useState(false);
  const [videos, setVideos] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const show = (m: string) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  useEffect(() => {
    (async () => {
      try {
        // --- Flow "code" : Google renvoie ?code=... dans l'URL ---
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const oauthError = params.get('error');

        if (oauthError) {
          setError("La connexion YouTube a été refusée ou annulée.");
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }

        if (code) {
          show('🔄 Connexion YouTube en cours...');
          const redirect = window.location.origin + '/youtube-callback';
          // Échange le code contre access_token + refresh_token via le Worker
          const res = await fetch(WORKER + '/youtube-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, redirect_uri: redirect }),
          });
          const data = await res.json();
          if (data.access_token) {
            localStorage.setItem('yt_access_token', data.access_token);
            localStorage.setItem('yt_token_expiry', String(Date.now() + (data.expires_in || 3600) * 1000));
            if (data.refresh_token) {
              localStorage.setItem('yt_refresh_token', data.refresh_token);
            }
            show('✅ YouTube connecté !');
            window.history.replaceState({}, '', window.location.pathname);
            setConnected(true);
            loadVideos(data.access_token);
            return;
          } else {
            setError(data.error || "Impossible de finaliser la connexion YouTube.");
            window.history.replaceState({}, '', window.location.pathname);
          }
        }

        // --- Compatibilité ancien flow "token" (hash #access_token) ---
        const hash = window.location.hash;
        if (hash.includes('access_token')) {
          const hp = new URLSearchParams(hash.substring(1));
          const token = hp.get('access_token');
          if (token) {
            localStorage.setItem('yt_access_token', token);
            localStorage.setItem('yt_token_expiry', String(Date.now() + 3600000));
            window.location.hash = '';
          }
        }

        // --- Vérifier un token déjà stocké ---
        const token = localStorage.getItem('yt_access_token');
        const expiry = localStorage.getItem('yt_token_expiry');
        if (token && expiry && Date.now() < parseInt(expiry)) {
          setConnected(true);
          loadVideos(token);
        } else if (token && localStorage.getItem('yt_refresh_token')) {
          // Token expiré mais on a un refresh_token → le Worker peut rafraîchir
          setConnected(true);
          refreshAndLoad();
        }
      } catch (e) {
        console.error('[YouTube] init:', e);
        setError("Erreur lors de l'initialisation YouTube.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAndLoad = async () => {
    try {
      const refresh_token = localStorage.getItem('yt_refresh_token');
      if (!refresh_token) return;
      const res = await fetch(WORKER + '/youtube-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('yt_access_token', data.access_token);
        localStorage.setItem('yt_token_expiry', String(Date.now() + (data.expires_in || 3600) * 1000));
        loadVideos(data.access_token);
      }
    } catch (e) {
      console.error('[YouTube] refresh:', e);
    }
  };

  const loadVideos = async (token: string) => {
    try {
      const res = await fetch('https://www.googleapis.com/youtube/v3/search?part=snippet&forMine=true&type=video&maxResults=10&order=date', { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { setError('Session YouTube expirée. Reconnectez-vous.'); return; }
      const data = await res.json();
      const ids = (data.items || []).map((i: any) => i.id.videoId).filter(Boolean).join(',');
      if (!ids) return;
      const statsRes = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics&id=${ids}`, { headers: { Authorization: `Bearer ${token}` } });
      const statsData = await statsRes.json();
      setVideos((statsData.items || []).map((v: any) => ({ id: v.id, title: v.snippet.title, thumbnail: v.snippet.thumbnails?.default?.url, views: v.statistics?.viewCount, likes: v.statistics?.likeCount, date: v.snippet.publishedAt })));
    } catch (e) {
      console.error('[YouTube] loadVideos:', e);
      show('⚠️ Impossible de charger vos vidéos.');
    }
  };

  const connectYT = () => {
    const redirect = window.location.origin + '/youtube-callback';
    const scope = 'https://www.googleapis.com/auth/youtube.upload https://www.googleapis.com/auth/youtube.readonly';
    // response_type=code + access_type=offline + prompt=consent → REFRESH TOKEN garanti
    const url = 'https://accounts.google.com/o/oauth2/v2/auth?' +
      'client_id=' + encodeURIComponent(YOUTUBE_CLIENT_ID) +
      '&redirect_uri=' + encodeURIComponent(redirect) +
      '&response_type=code' +
      '&access_type=offline' +
      '&prompt=consent' +
      '&scope=' + encodeURIComponent(scope);
    window.location.href = url;
  };

  const disconnectYT = () => {
    ['yt_access_token', 'yt_token_expiry', 'yt_refresh_token'].forEach(k => localStorage.removeItem(k));
    setConnected(false); setVideos([]);
    show('Déconnecté de YouTube');
  };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#F8F7FF', marginBottom: 6 }}>🔴 YouTube Shorts</div>
      <div style={{ color: '#C8C8E8', fontSize: 14, marginBottom: 20 }}>Publie et analyse tes Shorts</div>
      <ErrorBanner message={error} onDismiss={() => setError('')} />
      {!connected ? (
        <div style={{ background: '#1A1A2E', border: '1px solid #3A3A5C', borderRadius: 20, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔴</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#F8F7FF', marginBottom: 8 }}>Connecte ta chaîne YouTube</div>
          <div style={{ fontSize: 13, color: '#C8C8E8', marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>Autorise ContenuIA à publier des Shorts et lire tes statistiques. La connexion reste active grâce au refresh automatique.</div>
          <button onClick={connectYT} style={{ padding: '14px 32px', background: '#FF0000', border: 'none', borderRadius: 14, color: '#FFF', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>🔗 Connecter YouTube</button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 14, color: '#00F5A0', fontWeight: 700 }}>✅ YouTube connecté — {videos.length} vidéos</span>
            <button onClick={disconnectYT} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #3A3A5C', borderRadius: 10, color: '#C8C8E8', fontSize: 12, cursor: 'pointer' }}>Déconnecter</button>
          </div>
          {videos.map((v, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid #1E1E35', alignItems: 'center' }}>
              {v.thumbnail && <img src={v.thumbnail} alt="" style={{ width: 80, borderRadius: 8 }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#F8F7FF' }}>{v.title?.substring(0, 60)}</div>
                <div style={{ fontSize: 11, color: '#6C6C8A' }}>{v.views} vues · {v.likes} likes</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1A1A2E', border: '1px solid #00F5A0', borderRadius: 12, padding: '12px 20px', color: '#00F5A0', zIndex: 2000, fontSize: 13, opacity: toast ? 1 : 0, transition: 'all 0.3s', pointerEvents: 'none' }}>{toast}</div>
    </div>
  );
};
export default YouTubePage;
